import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DOMPurify from 'dompurify';
import {
    appLogoWrapperClassName,
    collapseButtonClassName,
    dynamicListClassName,
    lineChartClassName,
    tryAgainClassName
} from './constants';
import moment from 'moment';

const pixelsLeftSide = 20;

const removeElementsByClassName = (elementName: string, reportItem: HTMLElement) => {
    const elements = reportItem.getElementsByClassName(elementName);
    Array.from(elements).forEach((element) => element.remove());
};

const getWidthAndHeigthRelativeCanvas = (canvas: HTMLCanvasElement, pdfHeight: number, pdfWidth: number) => {
    let imgWidth = canvas.width,
        imgHeight = canvas.height;

    if (imgHeight > pdfHeight || imgWidth > pdfWidth) {
        const scaleFactor = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        imgWidth *= scaleFactor;
        imgHeight *= scaleFactor;
    }
    return [imgHeight, imgWidth];
};

const getFirstElementByClassName = (element: HTMLElement | Document, classname: string) =>
    element.getElementsByClassName(classname)?.[0] as HTMLElement | undefined;

const getDateText = (fromDate: number, toDate: number) => {
    let dateFormat = 'H:mm A MM/DD/YYYY';
    if (moment(fromDate).hours() === 0 && moment(fromDate).minutes() === 0) {
        dateFormat = 'MM/DD/YYYY';
    }

    const fromDateString = moment(fromDate).format(dateFormat);
    const toDateString = moment(toDate).format(dateFormat);
    return `${fromDateString} - ${toDateString}`;
};

const createHeader = (fromDate: number, toDate: number) => {
    const headerWrapper = document.createElement('div');
    headerWrapper.style.display = 'flex';
    headerWrapper.style.justifyContent = 'space-between';
    headerWrapper.style.marginBottom = '15px';
    headerWrapper.style.fontSize = '20px';
    headerWrapper.style.letterSpacing = '-3%';

    const appLogoDiv = getFirstElementByClassName(document, appLogoWrapperClassName);
    const sanitizedLogoHTML = DOMPurify.sanitize(appLogoDiv?.innerHTML ?? '');
    const appLogoEl = document.createElement('div');
    appLogoEl.innerHTML = sanitizedLogoHTML;
    headerWrapper.appendChild(appLogoEl);

    const dateTimeDiv = document.createElement('div');
    dateTimeDiv.textContent = getDateText(fromDate, toDate);
    headerWrapper.appendChild(dateTimeDiv);

    return headerWrapper;
};

const SAFE_INPUT_REGEX = /^[a-zA-Z0-9_-][a-zA-Z0-9_ -.]*$/;
const validateInput = (value: string, name: string) => {
    if (!SAFE_INPUT_REGEX.test(value)) {
        throw new Error(`Invalid ${name}: unsafe characters detected.`);
    }
};

export const exportSinglePDF = async (reportName: string, orientation: 'l' | 'p', contentClass: string,
    fromDate: number, toDate: number) => {
    validateInput(contentClass, 'contentClass');
    validateInput(reportName, 'reportName');

    const pdf = new jsPDF({
        orientation: orientation,
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling'],
        compress: true
    });
    const pdfHeight = pdf.internal.pageSize.getHeight(),
        pdfWidth = pdf.internal.pageSize.getWidth();

    const reportListDiv = document.getElementsByClassName(contentClass)[0];
    const reportCloneEl = reportListDiv.cloneNode(true) as HTMLElement;
    // Sanitize the cloned report element to prevent XSS from dynamic content
    if (reportCloneEl.innerHTML) {
        reportCloneEl.innerHTML = DOMPurify.sanitize(reportCloneEl.innerHTML);
    }

    const headerWrapper = createHeader(fromDate, toDate);

    // Create a temporary div with absolute positioning off-screen
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-99999px';
    document.body.appendChild(tempDiv);
    tempDiv.appendChild(reportCloneEl);

    // because canvas doesn't actually get the current width of report (it return small width) - we need to set the width
    // and We assume that changing the width of the table does not destroy the resolution.
    reportCloneEl.style.width = `${1500}px`;
    reportCloneEl.className = reportCloneEl.className + ' export-mode';

    // fix columns names cut on top
    const tableHeader = getFirstElementByClassName(reportCloneEl, 'table-header');
    if (tableHeader) tableHeader.style.marginTop = `3px`;
    reportCloneEl.insertBefore(headerWrapper, reportCloneEl.firstChild);

    // Remove the SVG with classname 'collapse-button' and try-again-message
    removeElementsByClassName(collapseButtonClassName, reportCloneEl);
    removeElementsByClassName(tryAgainClassName, reportCloneEl);
    removeElementsByClassName('export', reportCloneEl);
    removeElementsByClassName('action-btn', reportCloneEl);
    removeElementsByClassName('date-range-picker-selection-date', reportCloneEl);

    // take care a report with chart and different table (App Traffic report currnetly)
    let imgLineChart, imgDynamicList, lineChartCanvas, dynamicListCanvas;
    const lineChartElement = getFirstElementByClassName(reportCloneEl, lineChartClassName);
    if (lineChartElement) {
        const lineChartElementFromWindow = getFirstElementByClassName(window.document, lineChartClassName);
        if (lineChartElementFromWindow) {
            lineChartCanvas = await html2canvas(lineChartElementFromWindow);
            imgLineChart = lineChartCanvas.toDataURL('image/png');
            removeElementsByClassName(lineChartClassName, reportCloneEl); // remove it from the report origin and put it with right positions
        }
    }

    const dynamicListElement = getFirstElementByClassName(reportCloneEl, dynamicListClassName);
    if (dynamicListElement) {
        const dynamicListElementFromWindow = window.document.getElementsByClassName(dynamicListClassName)[0] as HTMLElement;
        const dynamicListAppsTableElement =
            getFirstElementByClassName(window.document, dynamicListClassName)?.getElementsByClassName('ReactVirtualized__Grid')?.[0] as HTMLElement | undefined;

        // for app list table
        if (dynamicListAppsTableElement) {
            const row = getFirstElementByClassName(window.document, dynamicListClassName)?.getElementsByClassName('row')?.[0] as HTMLElement | undefined;
            let originalScrollTop = 0;
            if (dynamicListAppsTableElement.scrollTop) {
                originalScrollTop = dynamicListAppsTableElement.scrollTop;
                dynamicListAppsTableElement.scrollTop = 0;
            }

            const originalHeight = dynamicListAppsTableElement.style.height;
            if (row) {
                dynamicListAppsTableElement.style.height = `${Number.parseInt(row.style.height) * 11}px`; // get 10 apps and one more for title
            }
            dynamicListCanvas = await html2canvas(dynamicListAppsTableElement);
            imgDynamicList = dynamicListCanvas.toDataURL('image/png');

            // return the origin stlye
            dynamicListAppsTableElement.style.height = originalHeight;
            if (dynamicListAppsTableElement.scrollTop) {
                dynamicListAppsTableElement.scrollTop = originalScrollTop;
            }
        }
        // for no result message
        else {
            dynamicListCanvas = await html2canvas(dynamicListElementFromWindow);
            imgDynamicList = dynamicListCanvas.toDataURL('image/png');
        }
        removeElementsByClassName(dynamicListClassName, reportCloneEl);
    }

    const canvas = await html2canvas(reportCloneEl);
    const imgData = canvas.toDataURL('image/png');
    const [imgHeight, imgWidth] = getWidthAndHeigthRelativeCanvas(canvas, pdfHeight, pdfWidth);

    const x = (pdfWidth - imgWidth) / 2;
    pdf.addImage(imgData, 'PNG', x, 0, imgWidth, imgHeight, '', 'FAST');

    // add the chart and dynamic table if we saved them before
    if (lineChartElement && lineChartCanvas && imgLineChart) {
        const [lineChartHeight, lineChartWidth] = getWidthAndHeigthRelativeCanvas(lineChartCanvas, pdfHeight, pdfWidth);
        pdf.addImage(imgLineChart, 'png', pixelsLeftSide, lineChartElement.offsetTop + 180, lineChartWidth * 0.95, lineChartHeight * 0.95);
    }
    if (dynamicListElement && dynamicListCanvas && imgDynamicList) {
        pdf.addPage();
        const [dynamicListHeight, dynamicListWidth] = getWidthAndHeigthRelativeCanvas(dynamicListCanvas, pdfHeight, pdfWidth);
        pdf.addImage(imgDynamicList, 'png', pixelsLeftSide, dynamicListElement.offsetTop + 10, dynamicListWidth, dynamicListHeight);
    }

    pdf.save(`${reportName}.pdf`);
    tempDiv.remove();
};